// ---------------------------------------------------------------------------
// AgentTypeField — dropdown selector for the agent runtime (LLM / HTTP Proxy
// / Custom), embedded inside the Agent module rather than its own card. Only
// "LLM" is implemented today; the other two render as disabled options with
// explanatory notes. Locked in edit mode because changing the type would
// require rebuilding the template's tasks.
// ---------------------------------------------------------------------------

import { Dropdown, Field, Option } from '@fluentui/react-components';

import { useStyles } from '../../EvaluationTemplateForm.style.js';
import type { AgentTypeChoice } from '../../EvaluationTemplateForm.types.js';

interface AgentTypeOption {
  value: AgentTypeChoice;
  title: string;
  description: string;
  /** Only LLM is implemented; the rest render disabled with a note. */
  disabled?: boolean;
  note?: string;
}

const AGENT_TYPE_OPTIONS: AgentTypeOption[] = [
  {
    value: 'llm',
    title: 'LLM Agent',
    description: 'GPT-4o, Claude, or any Azure OpenAI model',
  },
  {
    value: 'http-proxy',
    title: 'HTTP Proxy',
    description: 'Call a REST API or local Python service',
    disabled: true,
    note: 'Field shape not yet drawn',
  },
  {
    value: 'custom',
    title: 'Custom',
    description: 'Load via --extension assembly',
    disabled: true,
    note: 'Not supported by server',
  },
];

export interface AgentTypeFieldProps {
  agentType: AgentTypeChoice;
  /** True in edit mode — locks the dropdown. */
  locked: boolean;
  onChange: (next: AgentTypeChoice) => void;
}

export function AgentTypeField({
  agentType,
  locked,
  onChange,
}: AgentTypeFieldProps) {
  const styles = useStyles();
  const selected = AGENT_TYPE_OPTIONS.find((o) => o.value === agentType);
  return (
    <>
      <Field
        label="Agent type"
        orientation="horizontal"
        className={styles.inlineField}
      >
        <Dropdown
          aria-label="Agent type"
          disabled={locked}
          value={selected?.title ?? ''}
          selectedOptions={[agentType]}
          onOptionSelect={(_e, data) => {
            if (data.optionValue) {
              onChange(data.optionValue as AgentTypeChoice);
            }
          }}
        >
          {AGENT_TYPE_OPTIONS.map((opt) => (
            <Option
              key={opt.value}
              value={opt.value}
              text={opt.title}
              disabled={opt.disabled}
            >
              <div className={styles.optionStack}>
                <span className={styles.segmentTitle}>{opt.title}</span>
                <span className={styles.segmentDescription}>
                  {opt.description}
                </span>
                {opt.note && (
                  <span className={styles.segmentNote}>{opt.note}</span>
                )}
              </div>
            </Option>
          ))}
        </Dropdown>
      </Field>
      {agentType !== 'llm' && (
        <div className={styles.noteCallout}>
          This agent type is not implemented in v1. Switch to{' '}
          <strong>LLM Agent</strong> to continue.
        </div>
      )}
    </>
  );
}
